/**
 * Consent Management Platform — single source of truth.
 *
 * The consent *defaults* are set by the inline snippet at the very top of every
 * `<head>` (index.html, privacy/index.html, terms/index.html) so that Google
 * Consent Mode v2 is armed before any tag — including GA4 — has a chance to run.
 * That snippet only ever *reads* the cookie; this module is the only thing that
 * writes it.
 *
 * Categories
 *   necessary  always on — cannot be switched off
 *   analytics  Google Analytics 4  → analytics_storage
 *   marketing  email marketing + ad platforms
 *              → ad_storage, ad_user_data, ad_personalization, personalization_storage
 */

export type ConsentCategory = "analytics" | "marketing";

export interface ConsentState {
  /** Schema version. Bumping re-prompts every visitor. */
  v: number;
  analytics: boolean;
  marketing: boolean;
  /** Epoch ms the choice was recorded. */
  ts: number;
}

/**
 * Bump this when the category list or their meaning changes — every visitor
 * will be asked again. Must stay in sync with the `v` check in the inline
 * `<head>` snippet.
 */
export const CONSENT_VERSION = 1;

/**
 * GA4 measurement ID. Leave empty until GA4 is installed; once set, opting out
 * of analytics also flips Google's own `ga-disable-*` kill switch, which stops
 * gtag.js from sending anything at all.
 */
export const GA_MEASUREMENT_ID = "";

const COOKIE_NAME = "pss_consent";
const COOKIE_MAX_AGE_DAYS = 180;

const DENIED_STATE: Omit<ConsentState, "v" | "ts"> = {
  analytics: false,
  marketing: false,
};

/* ------------------------------------------------------------------ storage */

function readCookie(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"),
  );
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as ConsentState;
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    return {
      v: CONSENT_VERSION,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      ts: typeof parsed.ts === "number" ? parsed.ts : Date.now(),
    };
  } catch {
    return null;
  }
}

function writeCookie(state: ConsentState) {
  const value = encodeURIComponent(JSON.stringify(state));
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

/** Drop the cookies GA4 already dropped, so an opt-out takes effect now. */
function clearAnalyticsCookies() {
  const stale = document.cookie
    .split("; ")
    .map((c) => c.split("=")[0])
    .filter((name) => /^_ga($|_)|^_gid$|^_gat($|_)|^_gcl_/.test(name));

  // A cookie set on ".example.com" has to be expired on that same domain.
  const host = location.hostname;
  const domains = [undefined, host, `.${host}`];
  const parts = host.split(".");
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);

  for (const name of stale) {
    for (const domain of domains) {
      document.cookie =
        `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT` +
        (domain ? `; Domain=${domain}` : "");
    }
  }
}

/* --------------------------------------------------------------- google tag */

type GtagArgs =
  | ["consent", "default" | "update", Record<string, unknown>]
  | ["set", string, unknown]
  | [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
    /** Set by the inline `<head>` snippet for a returning visitor. */
    __pssConsent?: ConsentState;
    /** Lets a plain anchor or an external script reopen the panel. */
    openConsentPreferences?: () => void;
  }
}

function gtag(...args: GtagArgs) {
  // The inline snippet defines this. Fall back to a raw dataLayer push in case
  // this module somehow loads first (e.g. a page missing the snippet).
  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }
  (window.dataLayer = window.dataLayer || []).push(args);
}

/** Translate our two categories into Consent Mode v2 signals. */
function pushConsentUpdate(state: ConsentState) {
  const analytics = state.analytics ? "granted" : "denied";
  const marketing = state.marketing ? "granted" : "denied";

  gtag("consent", "update", {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    personalization_storage: marketing,
    functionality_storage: "granted",
    security_storage: "granted",
  });

  gtag("set", "ads_data_redaction", !state.marketing);

  if (GA_MEASUREMENT_ID) {
    // Hard kill switch honoured by gtag.js itself.
    (window as unknown as Record<string, boolean>)[
      `ga-disable-${GA_MEASUREMENT_ID}`
    ] = !state.analytics;
  }

  if (!state.analytics) clearAnalyticsCookies();

  // A single event other tools (GTM triggers, Klaviyo, etc.) can listen for.
  (window.dataLayer = window.dataLayer || []).push({
    event: "consent_update",
    consent_analytics: state.analytics,
    consent_marketing: state.marketing,
  });
}

/* ----------------------------------------------------------------- the store */

let snapshot: ConsentState | null = readCookie() ?? window.__pssConsent ?? null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Cached so `useSyncExternalStore` sees a stable reference. */
export function getConsent(): ConsentState | null {
  return snapshot;
}

/** `false` until the visitor has actually chosen. */
export function hasDecided(): boolean {
  return snapshot !== null;
}

export function isAllowed(category: ConsentCategory): boolean {
  return snapshot?.[category] === true;
}

/** Record a choice, sync it to Google Consent Mode, and notify subscribers. */
export function setConsent(choice: Record<ConsentCategory, boolean>) {
  const state: ConsentState = {
    v: CONSENT_VERSION,
    analytics: choice.analytics === true,
    marketing: choice.marketing === true,
    ts: Date.now(),
  };

  snapshot = state;
  window.__pssConsent = state;
  writeCookie(state);
  pushConsentUpdate(state);
  emit();
}

export function acceptAll() {
  setConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  setConsent({ ...DENIED_STATE });
}

/* ----------------------------------------------------- reopening the panel */

const OPEN_EVENT = "pss:consent-open";

export function openConsentPreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenConsentPreferences(handler: () => void): () => void {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

if (typeof window !== "undefined") {
  window.openConsentPreferences = openConsentPreferences;

  // A returning visitor's defaults were already applied by the inline snippet,
  // so there is nothing to replay here — only re-assert the GA kill switch,
  // which lives on `window` and does not survive a page load.
  if (snapshot && GA_MEASUREMENT_ID) {
    (window as unknown as Record<string, boolean>)[
      `ga-disable-${GA_MEASUREMENT_ID}`
    ] = !snapshot.analytics;
  }
}
